import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseModalDirective } from '@app/components/modals/base-modal';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from "@app/gateway/services/api.service";
import { StorageService } from '@app/services/storage/storage.service';
import { ProcessService } from '@app/services/process.service';
import Vs from '@app/services/validators/validators.service';


@Component({
  templateUrl: './confirm-phonenumber.component.html',
  styleUrls: ['./confirm-phonenumber.component.scss'],
  standalone: false
})
export class ConfirmPhoneNumberModalComponent extends BaseModalDirective implements OnInit, OnDestroy {
  newPhoneNumber: FormControl;
  confirmPhoneNumber: FormControl;
  currentView: 'do-confirm' | 'yes' | 'no' = 'do-confirm';
  displayPhoneNumber: string;
  phoneNumber: string;
  private destroy$ = new Subject<void>();

  constructor(
    protected modalRef: BsModalRef,
    protected builder: FormBuilder,
    public apiService: ApiService,
    protected storageService: StorageService,
    private processService: ProcessService
  ) {
    super(modalRef, builder);
  }

  ngOnInit(): void {
    this.showConfirm = false;
    this.showDecline = false;
    this.displayPhoneNumber = this.formatPhoneNumber(this.phoneNumber);
    this.newPhoneNumber = new FormControl('', [Vs.required, Vs.phoneNumber]);
    this.confirmPhoneNumber = new FormControl('', [Vs.required, Vs.phoneNumber]);
    this.newPhoneNumber.valueChanges.subscribe(() => this.validatePhoneNumbers());
    this.confirmPhoneNumber.valueChanges.subscribe(() => this.validatePhoneNumbers());
    if (this.displayPhoneNumber) {
      this.currentView = 'do-confirm';
    } else {
      this.currentView = 'no';
    }

    // Listen for session timeout or logout
    this.processService.getOnLogout().pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.modalRef.hide();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  onSubmit() {
    this.validatePhoneNumbers();

    if (!this.isFormValid()) {
      this.newPhoneNumber.markAsTouched();
      this.confirmPhoneNumber.markAsTouched();
      return;
    }

    // Prepare attestation payload
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    const indiv = sessionStorage.getItem('individualId');
    const currentLang = sessionStorage.getItem('currentLang');
    const globalLanguage = sessionStorage.getItem('globalTranslatedLan');
    let lang = globalLanguage? globalLanguage : currentLang;

    const phoneAttestationPayload = {
      phoneNumber: this.getDigitsOnly(this.newPhoneNumber.value),
      indvId: indiv,
      languagePreference: lang === 'en' ? 'EN' : 'SP',
      userName: currentUser?.userId,
      memberPhnAttestedSw: 'Y'
    };

    this.apiService.post('indi/users/updatePhoneNumAttestation', phoneAttestationPayload).subscribe({
      next: () => {
        this.hide({ action: 'submit', phoneNumber: this.newPhoneNumber.value });
      },
      error: () => {
        this.hide({ action: 'submit', phoneNumber: this.newPhoneNumber.value });
      }
    });
  }

  onCancel() {
    // Go back to the initial confirm view
    this.currentView = 'do-confirm';
    // Reset form values
    this.newPhoneNumber.reset();
    this.confirmPhoneNumber.reset();
    this.newPhoneNumber.markAsUntouched();
    this.confirmPhoneNumber.markAsUntouched();
  }

  onYes() {
    // Show thank you popup
    this.currentView = 'yes';
  }

  onNo() {
    // Show phone number update form
    this.currentView = 'no';
  }

  onCloseAcknowledgement() {
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    const indiv = sessionStorage.getItem('individualId');
    const currentLang = sessionStorage.getItem('currentLang');
    const globalLanguage = sessionStorage.getItem('globalTranslatedLan');
    let lang = globalLanguage? globalLanguage : currentLang;

    const phoneAttestationPayload = {
      phoneNumber: this.getDigitsOnly(this.displayPhoneNumber),
      indvId: indiv,
      languagePreference: lang === 'en' ? 'EN' : 'SP',
      userName: currentUser?.userId,
      memberPhnAttestedSw: 'Y'
    };

    this.apiService.post('indi/users/updatePhoneNumAttestation', phoneAttestationPayload).subscribe({
      next: () => {
        this.hide({ action: 'close', status: 'confirmed' });
      },
      error: () => {
        this.hide({ action: 'close', status: 'confirmed' });
      }
    });
  }


  validatePhoneNumbers() {
    const phoneNumber = this.getDigitsOnly(this.newPhoneNumber?.value);
    const confirmPhoneNumber = this.getDigitsOnly(this.confirmPhoneNumber?.value);

    this.applyPhoneNumberErrors(this.newPhoneNumber);
    this.applyPhoneNumberErrors(this.confirmPhoneNumber);

    if (
      this.newPhoneNumber?.valid &&
      this.confirmPhoneNumber?.valid &&
      phoneNumber !== confirmPhoneNumber
    ) {
      this.confirmPhoneNumber.setErrors({
        ...(this.confirmPhoneNumber.errors || {}),
        PHONE_NUMBERS_DO_NOT_MATCH: true
      });
      return;
    }

    if (this.confirmPhoneNumber?.errors?.PHONE_NUMBERS_DO_NOT_MATCH) {
      const { PHONE_NUMBERS_DO_NOT_MATCH, ...remainingErrors } = this.confirmPhoneNumber.errors;
      this.confirmPhoneNumber.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }

  isFormValid(): boolean {
    const phoneNumber = this.getDigitsOnly(this.newPhoneNumber?.value);
    const confirmPhoneNumber = this.getDigitsOnly(this.confirmPhoneNumber?.value);
    return this.newPhoneNumber?.valid && this.confirmPhoneNumber?.valid && phoneNumber === confirmPhoneNumber;
  }

  hasControlError(control: FormControl): boolean {
    return !!control?.touched && !!control?.errors;
  }

  getControlErrorMessage(control: FormControl, label: string): string {
    if (!control?.errors) {
      return '';
    }

    if (control.errors.required) {
      return `${label} is required.`;
    }

    if (control.errors.PHONE_NUMBER || control.errors.invalidPhoneNumber) {
      return `Enter a valid ${label.toLowerCase()}.`;
    }

    if (control.errors.PHONE_NUMBERS_DO_NOT_MATCH) {
      return 'Phone numbers do not match.';
    }

    return `Enter a valid ${label.toLowerCase()}.`;
  }

  private applyPhoneNumberErrors(control: FormControl) {
    if (!control) {
      return;
    }

    const digits = this.getDigitsOnly(control.value);

    if (!digits.length) {
      control.setErrors({ required: true });
      return;
    }

    if (digits.length < 10) {
      control.setErrors({ PHONE_NUMBER: true });
      return;
    }

    if (Vs.phoneNumber({ value: digits } as FormControl)) {
      control.setErrors({ PHONE_NUMBER: true });
      return;
    }

    control.setErrors(null);
  }

  private getDigitsOnly(value: string): string {
    return (value || '').replace(/\D/g, '');
  }

  private formatPhoneNumber(value: string): string {
    const digits = this.getDigitsOnly(value);

    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return value || '';
  }

}
